// src/components/auth/CompleteProfileModal.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  checkUsernameAvailability,
  sendSignupEmailVerificationCode,
} from '../../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Avatar,
  Alert,
  IconButton,
  CircularProgress,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

export interface ProfileData {
  displayName: string;
  username: string;
  email?: string; // Optional - only collected if not provided by Auth0
  bio?: string;
  socialProfile?: string; // Instagram, YouTube, Facebook, or Twitch URL
  profileImage?: string;
  profileImageFile?: File;
  emailVerificationCode?: string;
}

interface Props {
  open: boolean;
  onSubmit: (data: ProfileData) => void | Promise<void>;
  onCancel: () => void;
  defaultName?: string;
  defaultEmail?: string;
  defaultImage?: string;
  accountCreated?: boolean;
  requiresEmailVerification?: boolean;
}

const SUPPORTED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

const RESERVED_USERNAMES = new Set([
  'login',
  'signup',
  'explore',
  'dashboard',
  'admin',
  'settings',
  'messages',
  'applications',
  'create_guest_shoot',
  'my_shoots',
  'my_applications',
  'profile',
  'applicant',
  'host',
  'creator',
  'shoots',
  'guest_spot',
  'guest_shoot',
  'continue_application',
  'invite_signup',
  'auth0_callback',
  'application',
  'suggested_invites',
  'edit_guest_shoot',
  'create_guest_spot',
  'edit_guest_spot',
]);

const suggestedUsername = (name: string, email: string): string => {
  const source = name.trim() || email.split('@')[0] || '';
  const normalized = source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);
  return normalized.length >= 3 ? normalized : '';
};

const readableSignupError = (error: unknown): string => {
  const message = error instanceof Error ? error.message : '';
  if (/username.*(taken|exists|registered)/i.test(message)) {
    return 'That username was just taken. Choose another one and try again.';
  }
  if (
    /email.*(registered|already exists|another account)/i.test(message)
    || /(account|identity).*already.*email/i.test(message)
  ) {
    return 'An account already uses that email. Sign in with email, or use a different verified address.';
  }
  if (/(invalid|expired).*(code|otp)|code.*(invalid|expired)/i.test(message)) {
    return 'That verification code is invalid or expired. Request a new code and try again.';
  }
  if (/provider-verified email|verification code|required to create/i.test(message)) {
    return 'Verify your email with the code we sent before creating your account.';
  }
  return message || 'We could not finish setting up your account. Your information is still here—please try again.';
};

const CompleteProfileModal: React.FC<Props> = ({ 
  open, 
  onSubmit, 
  onCancel,
  defaultName = '',
  defaultEmail = '',
  defaultImage = '',
  accountCreated = false,
  requiresEmailVerification = false,
}) => {
  const [displayName, setDisplayName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [username, setUsername] = useState(() => suggestedUsername(defaultName, defaultEmail));
  const [bio, setBio] = useState('');
  const [socialProfile, setSocialProfile] = useState('');
  const [profileImage, setProfileImage] = useState(defaultImage);
  const [profileImageFile, setProfileImageFile] = useState<File | undefined>();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [usernameCheckError, setUsernameCheckError] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [emailCodeMessage, setEmailCodeMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const previewUrlRef = useRef<string | null>(null);
  
  // Track if email is required (when not provided by Auth0)
  const needsEmail = requiresEmailVerification || !defaultEmail.trim();

  const clearPreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  // The dialog is permanently mounted. Rehydrate it for each newly pending
  // Auth0 identity so Facebook name/photo data cannot be blank or leak across
  // signup attempts.
  useEffect(() => {
    if (!open) {
      clearPreviewUrl();
      return;
    }
    clearPreviewUrl();
    setDisplayName(defaultName);
    setEmail(defaultEmail);
    setUsername(suggestedUsername(defaultName, defaultEmail));
    setBio('');
    setSocialProfile('');
    setProfileImage(defaultImage);
    setProfileImageFile(undefined);
    setErrors({});
    setCheckingUsername(false);
    setUsernameAvailable(null);
    setUsernameCheckError('');
    setEmailCodeSent(false);
    setEmailVerificationCode('');
    setSendingEmailCode(false);
    setEmailCodeMessage('');
    setSubmitError('');
    setSubmitting(false);
  }, [open, defaultName, defaultEmail, defaultImage, clearPreviewUrl]);

  useEffect(() => () => clearPreviewUrl(), [clearPreviewUrl]);

  // Check username availability with debounce
  useEffect(() => {
    const normalizedUsername = username.trim();
    if (!open || !normalizedUsername || normalizedUsername.length < 3) {
      setCheckingUsername(false);
      setUsernameAvailable(null);
      setUsernameCheckError('');
      return;
    }

    // Basic validation first
    if (
      normalizedUsername.length > 100 ||
      !/^[a-zA-Z0-9_]+$/.test(normalizedUsername) ||
      /^\d+$/.test(normalizedUsername) ||
      RESERVED_USERNAMES.has(normalizedUsername.toLowerCase())
    ) {
      setCheckingUsername(false);
      setUsernameAvailable(null);
      setUsernameCheckError('');
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setCheckingUsername(true);
      setUsernameCheckError('');
      try {
        const isAvailable = await checkUsernameAvailability(normalizedUsername);
        if (!cancelled) setUsernameAvailable(isAvailable);
      } catch (error) {
        console.error('Error checking username:', error);
        if (!cancelled) {
          setUsernameAvailable(null);
          setUsernameCheckError('We could not check that username. Try again in a moment.');
        }
      } finally {
        if (!cancelled) setCheckingUsername(false);
      }
    }, 500); // 500ms debounce

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, username]);

  const handleImageSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!SUPPORTED_IMAGE_TYPES.has(file.type)) {
      setErrors((current) => ({
        ...current,
        profileImage: 'Use a JPG, PNG, GIF, or WebP image.',
      }));
      return;
    }

    if (file.size === 0) {
      setErrors((current) => ({
        ...current,
        profileImage: 'That image is empty. Choose another file.',
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((current) => ({
        ...current,
        profileImage: 'Choose an image smaller than 5 MB.',
      }));
      return;
    }

    clearPreviewUrl();
    const previewUrl = URL.createObjectURL(file);
    previewUrlRef.current = previewUrl;
    setProfileImage(previewUrl);
    setProfileImageFile(file);
    setErrors((current) => ({ ...current, profileImage: '' }));
    setSubmitError('');
  };

  const removeSelectedImage = () => {
    clearPreviewUrl();
    setProfileImageFile(undefined);
    setProfileImage(defaultImage);
    setErrors((current) => ({ ...current, profileImage: '' }));
  };

  const handleSendEmailCode = async () => {
    const normalizedEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setErrors((current) => ({
        ...current,
        email: normalizedEmail ? 'Please enter a valid email address' : 'Email is required',
      }));
      return;
    }

    setSendingEmailCode(true);
    setEmailCodeMessage('');
    setSubmitError('');
    try {
      await sendSignupEmailVerificationCode(normalizedEmail);
      setEmailCodeSent(true);
      setEmailVerificationCode('');
      setEmailCodeMessage(`Code sent to ${normalizedEmail}`);
    } catch (error) {
      setEmailCodeSent(false);
      setEmailCodeMessage('');
      setSubmitError(readableSignupError(error));
    } finally {
      setSendingEmailCode(false);
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (errors.profileImage) {
      newErrors.profileImage = errors.profileImage;
    }

    if (!displayName.trim()) {
      newErrors.displayName = 'Name is required';
    }
    
    // Email validation - only required if not provided by Auth0
    if (needsEmail) {
      if (!email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        newErrors.email = 'Please enter a valid email address';
      } else if (!emailCodeSent) {
        newErrors.emailVerificationCode = 'Send a verification code to confirm this email';
      } else if (!/^\d{6}$/.test(emailVerificationCode)) {
        newErrors.emailVerificationCode = 'Enter the 6-digit code from your email';
      }
    }

    if (!username.trim()) {
      newErrors.username = 'Username is required';
    } else if (username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (username.trim().length > 100) {
      newErrors.username = 'Username must be 100 characters or fewer';
    } else if (!/^[a-zA-Z0-9_]+$/.test(username.trim())) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    } else if (/^\d+$/.test(username.trim())) {
      newErrors.username = 'Username cannot be all numbers';
    } else if (RESERVED_USERNAMES.has(username.trim().toLowerCase())) {
      newErrors.username = 'That username is reserved. Please choose another one.';
    } else if (usernameAvailable === false) {
      newErrors.username = 'Username is already taken. Please choose another one.';
    } else if (usernameCheckError) {
      newErrors.username = usernameCheckError;
    } else if (checkingUsername || (usernameAvailable === null && username.trim().length >= 3)) {
      newErrors.username = 'Checking username availability...';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (submitting || !validateForm()) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      await onSubmit({
        displayName: displayName.trim(),
        username: username.trim(),
        email: needsEmail ? email.trim() : undefined,
        bio: bio.trim() || undefined,
        socialProfile: socialProfile.trim() || undefined,
        profileImage: profileImageFile ? defaultImage : profileImage,
        profileImageFile,
        emailVerificationCode: needsEmail ? emailVerificationCode : undefined,
      });
    } catch (error) {
      setSubmitError(readableSignupError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => {
        if (!submitting) onCancel();
      }}
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ fontWeight: 600, color: '#2D2070', fontSize: '1.5rem', lineHeight: 1.334 }}>
          Welcome! 👋
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {accountCreated
            ? 'Your account is ready—finish adding your optional photo'
            : needsEmail
            ? 'Verify your email, then choose your username'
            : 'Choose your username and confirm your profile'
          }
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {needsEmail && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Facebook did not share a verified email. We’ll send you a code so
            your account and booking updates stay secure.
          </Alert>
        )}
        
        {/* Profile Photo */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: errors.profileImage ? 1 : 3 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar 
              src={profileImage} 
              sx={{ width: 80, height: 80, mr: 2 }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <input
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              id="profile-image-upload"
              type="file"
              onChange={handleImageSelection}
            />
            <label htmlFor="profile-image-upload">
              <IconButton
                component="span"
                disabled={submitting}
                aria-label="Choose profile photo"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 16,
                  bgcolor: '#2D2070',
                  color: 'white',
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: '#1f1550' },
                  boxShadow: 2
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </label>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Add a photo (optional). It uploads securely after your account is created.
            </Typography>
            {profileImageFile && (
              <Button
                size="small"
                color="inherit"
                onClick={removeSelectedImage}
                disabled={submitting}
                sx={{ mt: 0.5, px: 0, minWidth: 0, textTransform: 'none' }}
              >
                Remove selected photo
              </Button>
            )}
          </Box>
        </Box>
        {errors.profileImage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.profileImage}
          </Alert>
        )}

        {/* Display Name */}
        <TextField
          fullWidth
          label="Name"
          value={displayName}
          disabled={accountCreated || submitting}
          onChange={(e) => {
            setDisplayName(e.target.value);
            if (errors.displayName) {
              setErrors({ ...errors, displayName: '' });
            }
          }}
          error={!!errors.displayName}
          helperText={errors.displayName}
          sx={{ mb: 2 }}
        />

        {/* Email - only show if not provided by Auth0 */}
        {needsEmail && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="your@email.com"
              value={email}
              disabled={accountCreated || sendingEmailCode || submitting}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailCodeSent(false);
                setEmailVerificationCode('');
                setEmailCodeMessage('');
                setErrors((current) => ({
                  ...current,
                  email: '',
                  emailVerificationCode: '',
                }));
              }}
              error={!!errors.email}
              helperText={errors.email}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleSendEmailCode}
              disabled={accountCreated || sendingEmailCode || submitting || !email.trim()}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              {sendingEmailCode ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Sending code…
                </>
              ) : emailCodeSent ? 'Send a new code' : 'Send verification code'}
            </Button>
            {emailCodeMessage && (
              <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                {emailCodeMessage}
              </Typography>
            )}

            {emailCodeSent && (
              <TextField
                fullWidth
                label="6-digit verification code"
                value={emailVerificationCode}
                autoComplete="one-time-code"
                autoFocus
                disabled={accountCreated || submitting}
                onChange={(e) => {
                  setEmailVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  setErrors((current) => ({ ...current, emailVerificationCode: '' }));
                }}
                error={!!errors.emailVerificationCode}
                helperText={errors.emailVerificationCode || 'Check your inbox and spam folder.'}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 6,
                  style: { letterSpacing: '0.35em' },
                }}
                sx={{ mt: 2 }}
              />
            )}
            {!emailCodeSent && errors.emailVerificationCode && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.75 }}>
                {errors.emailVerificationCode}
              </Typography>
            )}
          </Box>
        )}

        {/* Username */}
        <TextField
          fullWidth
          label="Username"
          placeholder="e.g. john_doe"
          value={username}
          disabled={accountCreated || submitting}
          inputProps={{ maxLength: 100 }}
          onChange={(e) => {
            setUsername(e.target.value);
            setUsernameAvailable(null); // Reset when typing
            setUsernameCheckError('');
            setErrors((current) => ({ ...current, username: '' }));
          }}
          error={!!errors.username || usernameAvailable === false || !!usernameCheckError}
          helperText={
            errors.username ||
            usernameCheckError ||
            (checkingUsername ? 'Checking...' :
             usernameAvailable === true ? '✓ Available' :
             usernameAvailable === false ? 'Already taken' :
             '')
          }
          sx={{ 
            mb: 2,
            '& .MuiFormHelperText-root': {
              color: usernameAvailable === true ? '#4caf50' : 
                     usernameAvailable === false ? '#f44336' : undefined
            }
          }}
        />

        {/* Social Profile - Optional */}
        <TextField
          fullWidth
          label="Social Media (Optional)"
          placeholder="instagram.com/you or @yourhandle"
          value={socialProfile}
          disabled={accountCreated || submitting}
          onChange={(e) => setSocialProfile(e.target.value)}
          helperText="Instagram, YouTube, Facebook, or Twitch"
          sx={{ mb: 1 }}
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onCancel} color="inherit" disabled={submitting}>
          {accountCreated ? 'Continue without photo' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            checkingUsername ||
            usernameAvailable === false ||
            sendingEmailCode ||
            submitting ||
            (needsEmail && !accountCreated && (!emailCodeSent || emailVerificationCode.length !== 6))
          }
          sx={{ 
            bgcolor: '#2D2070', 
            '&:hover': { bgcolor: '#1f1550' },
            minWidth: 120
          }}
        >
          {checkingUsername
            ? 'Checking…'
            : submitting
              ? accountCreated ? 'Saving photo…' : 'Creating account…'
              : accountCreated
                ? profileImageFile ? 'Retry photo' : 'Continue'
                : 'Get Started'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteProfileModal;
